import { useState } from "react";
import { Search, MapPin, Calendar, Users, Minus, Plus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";
import { useBooking } from "@/context/BookingContext";
import { LOCATIONS } from "@/data";

export const SearchBar = () => {
  const { t } = useLang();
  const { setPrefill } = useBooking();
  const s = t.search;

  const [tripType, setTripType] = useState("one_way");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);

  const handleSearch = () => {
    setPrefill({
      trip_type: tripType,
      origin,
      destination,
      date,
      return_date: tripType === "round_trip" ? returnDate : "",
      adults,
      children,
      _ts: Date.now(),
    });
    document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" });
  };

  const Counter = ({ id, label, value, set, min }) => (
    <div className="flex items-center justify-between py-2">
      <span className="font-body text-sm text-jungle">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => set(Math.max(min, value - 1))}
          className="w-8 h-8 flex items-center justify-center border border-border text-jungle hover:border-teal hover:text-teal transition-colors"
          data-testid={`counter-minus-${id}`}
        >
          <Minus size={14} />
        </button>
        <span className="font-body text-sm w-5 text-center text-jungle" data-testid={`counter-value-${id}`}>{value}</span>
        <button
          type="button"
          onClick={() => set(value + 1)}
          className="w-8 h-8 flex items-center justify-center border border-border text-jungle hover:border-teal hover:text-teal transition-colors"
          data-testid={`counter-plus-${id}`}
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );

  const inputCls = "w-full bg-white border border-border px-4 py-3 font-body text-sm text-jungle placeholder:text-jungle-light/50 focus:outline-none focus:border-teal transition-colors";
  const labelCls = "font-body text-xs font-semibold text-jungle mb-1.5 block";

  return (
    <section className="relative z-20 -mt-24 md:-mt-28 px-6 md:px-12 lg:px-20" data-testid="search-bar-section">
      <div className="mx-auto max-w-6xl">
        <div className="bg-white border border-border shadow-2xl p-5 md:p-7">
          {/* trip type tabs */}
          <div className="flex items-center gap-6 border-b border-border mb-5">
            {[
              ["one_way", s.oneWay],
              ["round_trip", s.roundTrip],
            ].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setTripType(val)}
                data-testid={`triptype-${val}`}
                className={`font-body text-sm pb-3 -mb-px border-b-2 transition-colors ${
                  tripType === val
                    ? "border-teal text-jungle font-semibold"
                    : "border-transparent text-jungle-light/60 hover:text-jungle"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
            <div className="lg:col-span-3">
              <label className={labelCls}><MapPin size={12} className="inline mr-1 text-teal" />{s.from}</label>
              <input
                list="locations-list"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder={s.fromPlaceholder}
                className={inputCls}
                data-testid="search-origin"
              />
            </div>
            <div className="lg:col-span-3">
              <label className={labelCls}><MapPin size={12} className="inline mr-1 text-teal" />{s.to}</label>
              <input
                list="locations-list"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={s.toPlaceholder}
                className={inputCls}
                data-testid="search-destination"
              />
            </div>
            <div className={tripType === "round_trip" ? "lg:col-span-2" : "lg:col-span-3"}>
              <label className={labelCls}><Calendar size={12} className="inline mr-1 text-teal" />{s.pickupDate}</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="search-date" />
            </div>
            {tripType === "round_trip" && (
              <div className="lg:col-span-2">
                <label className={labelCls}><Calendar size={12} className="inline mr-1 text-teal" />{s.returnDate}</label>
                <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className={inputCls} data-testid="search-return-date" />
              </div>
            )}
            <div className={tripType === "round_trip" ? "lg:col-span-2" : "lg:col-span-3"}>
              <label className={labelCls}><Users size={12} className="inline mr-1 text-teal" />{s.passengers}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button className={`${inputCls} text-left`} data-testid="search-passengers-trigger">
                    {adults} {s.adults}{children > 0 ? `, ${children} ${s.children}` : ""}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-4" data-testid="passengers-popover">
                  <Counter id="adults" label={s.adults} value={adults} set={setAdults} min={1} />
                  <Counter id="children" label={s.children} value={children} set={setChildren} min={0} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <datalist id="locations-list">
            {LOCATIONS.map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5">
            <p className="font-body text-xs text-jungle-light/60">{s.trust}</p>
            <button
              onClick={handleSearch}
              data-testid="search-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-teal text-sand hover:bg-teal-dark font-body font-semibold px-10 py-4 transition-colors"
            >
              <Search size={18} /> {s.search}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
